import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const EnergyEfficiency = () => {
  // State variables for Energy Efficiency calculation
  const [inputEnergy, setInputEnergy] = useState(0); // Input Energy
  const [outputEnergy, setOutputEnergy] = useState(0); // Output Energy
  const [efficiency, setEfficiency] = useState(0); // Calculated efficiency percentage

  // Event handlers to update state values
  const handleInputEnergyChange = (e) =>
    setInputEnergy(parseFloat(e.target.value) || 0);
  const handleOutputEnergyChange = (e) =>
    setOutputEnergy(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate energy efficiency when any input changes
  useEffect(() => {
    if (inputEnergy > 0) {
      const calcEfficiency = (outputEnergy / inputEnergy) * 100;
      setEfficiency(calcEfficiency);
    } else {
      setEfficiency(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [inputEnergy, outputEnergy]);

  return (
    <Layout title="Energy Efficiency Calculator" path="energy-efficiency">
      <section className="section-padding" id="energy-efficiency-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Energy Efficiency Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Energy Efficiency Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input Energy */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="inputEnergy" className="form-label">
                        Input Energy (kWh)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Input Energy:</strong> The total energy supplied to the system in kilowatt-hours (kWh)."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="inputEnergy"
                        placeholder="Enter input energy"
                        value={inputEnergy || ""}
                        onChange={handleInputEnergyChange}
                      />
                    </div>

                    {/* Output Energy */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="outputEnergy" className="form-label">
                        Output Energy (kWh)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Output Energy:</strong> The usable energy output from the system in kilowatt-hours (kWh)."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="outputEnergy"
                        placeholder="Enter output energy"
                        value={outputEnergy || ""}
                        onChange={handleOutputEnergyChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Energy Efficiency:{" "}
                        <span className="fw-bold ms-3">
                          {efficiency === 0
                            ? "0.00"
                            : efficiency.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Energy Efficiency Calculator
                </h3>

                {/* What is an Energy Efficiency Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is an Energy Efficiency Calculator?
                </h4>
                <p>
                  An **Energy Efficiency Calculator** helps you determine the
                  percentage of output energy relative to the input energy of
                  equipment or processes. This allows you to assess how
                  effectively energy is being used and identify areas for
                  improvement.
                </p>

                {/* Why Use an Energy Efficiency Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use an Energy Efficiency Calculator?
                </h4>
                <p>
                  Using this calculator is essential for businesses to improve
                  energy consumption, reduce waste, and enhance sustainability
                  efforts. It can help you understand how much energy is
                  effectively utilized, which directly contributes to cost
                  savings and eco-friendly initiatives.
                </p>

                {/* Benefits of Using an Energy Efficiency Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using an Energy Efficiency Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Helps Reduce Energy Costs:</strong> Identifies
                    inefficiencies and areas for energy optimization.
                  </li>
                  <li>
                    <strong>Supports Sustainability Goals:</strong> Allows
                    businesses to track and reduce their environmental impact.
                  </li>
                  <li>
                    <strong>Aids in Compliance:</strong> Helps ensure energy
                    usage is aligned with environmental regulations and
                    policies.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use an Energy Efficiency Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Manufacturers:</strong> Helps track energy usage in
                    production processes and identify cost-saving opportunities.
                  </li>
                  <li>
                    <strong>Utilities:</strong> Assists in measuring energy
                    efficiency across operations and equipment.
                  </li>
                  <li>
                    <strong>Facilities Management:</strong> Useful for
                    monitoring and improving energy consumption in buildings and
                    infrastructure.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  What is a good energy efficiency ratio?
                </h4>
                <p>
                  A higher energy efficiency ratio is generally better.
                  Typically, achieving an efficiency of 80% or higher is
                  considered very good, depending on the type of equipment or
                  process being assessed.
                </p>

                <h4 className="mt-4 mb-3">
                  How can I improve energy efficiency?
                </h4>
                <p>
                  To improve energy efficiency, consider optimizing equipment,
                  reducing waste, upgrading to energy-efficient technology, and
                  implementing regular maintenance and monitoring programs.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is energy efficiency important?
                </h4>
                <p>
                  Energy efficiency is critical for reducing operational costs,
                  lowering carbon footprints, and meeting environmental
                  regulations. It also plays a key role in achieving long-term
                  sustainability goals for businesses and industries.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EnergyEfficiency;
